import { useState, useEffect } from 'react';
import { settingsApi } from '../services/api/endpoints/settings';
import type { 
  AppSettings, 
  SettingsUpdateRequest,
  DataSharingRequest,
  DataSourceUpdateRequest
} from '../types/settings';

export function useSettings() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Chargement des paramètres depuis l\'API...');
      const response = await settingsApi.getSettings();
      console.log('📥 Réponse API getSettings:', response);
      
      if (response.success && response.data) {
        console.log('✅ Paramètres chargés avec succès');
        setSettings(response.data);
      } else {
        console.warn('⚠️ API a retourné success=false ou data=null');
        // Fallback vers les paramètres par défaut
        const defaultSettings: AppSettings = {
          general: {
            language: 'fr',
            dateFormat: 'DD/MM/YYYY',
            timezone: 'Africa/Kinshasa',
            theme: 'light',
            baseCurrency: 'CDF',
            displayCurrency: 'CDF',
            exchangeRates: {
              USD: 0.0005,
              EUR: 0.00045
            }
          },
          accounting: {
            defaultJournal: 'OD',
            autoNumbering: true,
            voucherPrefix: 'VCH-',
            fiscalYearPattern: 'YYYY',
            accountingFramework: 'SYSCOHADA_NORMAL',
            defaultDepreciationMethod: 'linear',
            defaultVatRate: 18,
            journalEntryValidation: 'manual',
            accountingLevels: [
              { level: 1, name: 'Classe', digits: 1 },
              { level: 2, name: 'Compte Principal', digits: 2 },
              { level: 3, name: 'Compte Divisionnaire', digits: 3 },
              { level: 4, name: 'Sous-compte', digits: 5 }
            ]
          },
          security: {
            twoFactorEnabled: false,
            passwordPolicy: {
              minLength: 8,
              requireUppercase: true,
              requireNumbers: true,
              requireSymbols: false
            },
            sessionTimeout: 30,
            auditLogRetention: 90
          },
          notifications: {
            journal_validation: { email: true, browser: true },
            report_generation: { email: false, browser: true },
            user_mention: { email: true, browser: true },
            system_alerts: { email: true, browser: true },
            fiscal_year_closing: { email: true, browser: true }
          },
          integrations: {
            googleDrive: {
              enabled: false,
              linkedAccount: null
            },
            ksPay: {
              enabled: true,
              apiKey: 'ks_demo_key'
            },
            slack: {
              enabled: false,
              webhookUrl: null
            },
            dataSharing: {
              banks: false,
              microfinance: false,
              coopec: false,
              analysts: false,
              partners: false,
              consentGiven: false
            },
            dataSources: {
              sources: [
                {
                  id: 'wanzo-mobile',
                  name: 'Wanzo Mobile',
                  description: 'Application mobile de gestion',
                  icon: 'Smartphone',
                  isConnected: false,
                  isConfigurable: true
                }
              ]
            },
            bankIntegrations: [],
            eInvoicing: {
              provider: 'dgi_congo',
              enabled: false,
              taxpayerNumber: '',
              autoSubmit: false,
              validateBeforeSubmit: true,
              syncInvoices: false
            },
            taxIntegration: {
              dgiEnabled: false,
              taxpayerNumber: '',
              autoCalculateTax: true,
              taxRates: [
                { type: 'VAT', rate: 18, description: 'TVA Standard' }
              ],
              declarationFrequency: 'monthly'
            },
            portfolioIntegration: {
              enabled: false,
              portfolioTypes: [],
              automaticValuation: false,
              valuationFrequency: 'monthly',
              currencyConversion: true
            }
          }
        };
        setSettings(defaultSettings);
        setError('API non disponible, utilisation des paramètres par défaut');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des paramètres:', err);
      setError('Erreur lors du chargement des paramètres');
      
      // Fallback vers localStorage ou paramètres par défaut
      try {
        const stored = localStorage.getItem('app-settings');
        if (stored) {
          setSettings(JSON.parse(stored));
        }
      } catch (storageErr) {
        console.error('Erreur localStorage:', storageErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (request: SettingsUpdateRequest) => {
    try {
      console.log('🔄 Mise à jour des paramètres:', request);
      const response = await settingsApi.updateSettings(request);
      console.log('📥 Réponse API updateSettings:', response);
      
      if (response.success && response.data) {
        console.log('✅ Paramètres mis à jour avec succès');
        setSettings(response.data);
        // Sauvegarder en localStorage comme backup
        localStorage.setItem('app-settings', JSON.stringify(response.data));
        return response.data;
      } else {
        console.error('❌ Échec de la mise à jour:', response.error);
        throw new Error(response.error || 'Erreur lors de la mise à jour des paramètres');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour des paramètres:', err);
      
      // Fallback vers la mise à jour locale
      if (settings) {
        const updatedSettings = {
          ...settings,
          [request.category]: {
            ...settings[request.category],
            ...request.settings
          }
        };
        setSettings(updatedSettings);
        localStorage.setItem('app-settings', JSON.stringify(updatedSettings));
        return updatedSettings;
      }
      throw err;
    }
  };

  const getSettingsCategory = async (category: keyof AppSettings) => {
    try {
      const response = await settingsApi.getSettingsCategory(category);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        // Fallback vers les paramètres locaux
        return settings?.[category] || null;
      }
    } catch (err) {
      console.error(`Erreur lors du chargement de la catégorie ${category}:`, err);
      return settings?.[category] || null;
    }
  };

  const resetSettings = async (category?: keyof AppSettings) => {
    try {
      const response = await settingsApi.resetSettings(category);
      
      if (response.success && response.data) {
        setSettings(response.data);
        localStorage.setItem('app-settings', JSON.stringify(response.data));
        return response.data;
      } else {
        throw new Error(response.error || 'Erreur lors de la réinitialisation');
      }
    } catch (err) {
      console.error('Erreur lors de la réinitialisation:', err);
      throw err;
    }
  };

  const validateSettings = async (settingsToValidate: Partial<AppSettings>) => {
    try {
      const response = await settingsApi.validateSettings(settingsToValidate);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Erreur lors de la validation');
      }
    } catch (err) {
      console.error('Erreur lors de la validation:', err);
      // Validation basique locale
      return { valid: true, errors: [] };
    }
  };

  // === NOUVELLES FONCTIONS POUR LES INTÉGRATIONS AVANCÉES ===
  
  const getDataSharingSettings = async () => {
    try {
      const response = await settingsApi.getDataSharingSettings();
      if (response.success && response.data) {
        return response.data;
      }
      return settings?.integrations?.dataSharing || null;
    } catch (err) {
      console.error('Erreur lors du chargement des paramètres de partage:', err);
      return settings?.integrations?.dataSharing || null;
    }
  };

  const updateDataSharingSettings = async (request: DataSharingRequest) => {
    try {
      const response = await settingsApi.updateDataSharingSettings(request);
      if (response.success && response.data) {
        // Mettre à jour les settings locaux
        if (settings) {
          const updatedSettings = {
            ...settings,
            integrations: {
              ...settings.integrations,
              dataSharing: response.data
            }
          };
          setSettings(updatedSettings);
          localStorage.setItem('app-settings', JSON.stringify(updatedSettings));
        }
        return response.data;
      }
      throw new Error('Erreur lors de la mise à jour');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du partage de données:', err);
      throw err;
    }
  };

  const getDataSourceSettings = async () => {
    try {
      const response = await settingsApi.getDataSourceSettings();
      if (response.success && response.data) {
        return response.data;
      }
      return settings?.integrations?.dataSources || null;
    } catch (err) {
      console.error('Erreur lors du chargement des sources de données:', err);
      return settings?.integrations?.dataSources || null;
    }
  };

  const updateDataSourceSettings = async (request: DataSourceUpdateRequest) => {
    try {
      const response = await settingsApi.updateDataSourceSettings(request);
      if (response.success && response.data) {
        // Mettre à jour les settings locaux
        if (settings) {
          const updatedSettings = {
            ...settings,
            integrations: {
              ...settings.integrations,
              dataSources: response.data
            }
          };
          setSettings(updatedSettings);
          localStorage.setItem('app-settings', JSON.stringify(updatedSettings));
        }
        return response.data;
      }
      throw new Error('Erreur lors de la mise à jour');
    } catch (err) {
      console.error('Erreur lors de la mise à jour des sources de données:', err);
      throw err;
    }
  };

  const getIntegrationStatus = async () => {
    try {
      const response = await settingsApi.getIntegrationStatus();
      if (response.success && response.data) {
        return response.data;
      }
      return { integrations: [] };
    } catch (err) {
      console.error('Erreur lors du chargement du statut des intégrations:', err);
      return { integrations: [] };
    }
  };

  return {
    settings,
    loading,
    error,
    updateSettings,
    getSettingsCategory,
    resetSettings,
    validateSettings,
    refreshSettings: loadSettings,
    // Nouvelles fonctions pour les intégrations avancées
    getDataSharingSettings,
    updateDataSharingSettings,
    getDataSourceSettings,
    updateDataSourceSettings,
    getIntegrationStatus
  };
}
